var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import Typography from '@hellodarwin/core/lib/components/common/typography';
import Tooltip from 'antd/es/tooltip';
import SortedTable from '../sort-table';
import { TaskTableContainer, TaskTableOverflowTooltipList, TaskTableOwnerPill, TaskTableOwnerPillsContainer, TaskTableToggleButton, TaskTableToggleGroup, } from './styles';
export { TaskTableCellText, TaskTableCenteredCell, TaskTableFilterBar, TaskTableFilterDivider, TaskTableMutedText, TaskTableStatusPill, TaskTableTitleButton, TaskTableTitleText, } from './styles';
const TASK_TABLE_TOGGLE_COUNT_LIMIT = 99;
const COMPLETION_COLUMN_WIDTH = 52;
const TASK_COLUMN_WIDTH = 300;
const TASK_COLUMN_WIDTH_WITH_COMPLETED_DATE = 288;
const ASSIGNEE_COLUMN_WIDTH = 180;
const ASSIGNED_BY_COLUMN_WIDTH = 180;
const ASSOCIATED_OBJECTS_COLUMN_WIDTH = 460;
const ASSOCIATED_OBJECTS_COLUMN_WIDTH_WITH_COMPLETED_DATE = 428;
const DUE_DATE_COLUMN_WIDTH = 112;
const COMPLETED_DATE_COLUMN_WIDTH = 156;
const MAX_VISIBLE_OWNER_PILLS = 2;
const getColumnWidthPercent = (columnWidth, totalWidth) => `${(columnWidth / totalWidth) * 100}%`;
export const buildTaskTableColumns = ({ status, showAssignee = true, showAssignedBy = true, showAssociatedObjects = true, associatedObjectsColumnWidth: associatedObjectsColumnWidthOverride, labels, }) => {
    const showDueDate = status !== 'completed';
    const showCompletedDate = status !== 'open';
    const taskColumnWidth = showCompletedDate
        ? TASK_COLUMN_WIDTH_WITH_COMPLETED_DATE
        : TASK_COLUMN_WIDTH;
    const associatedObjectsColumnWidth = associatedObjectsColumnWidthOverride !== null && associatedObjectsColumnWidthOverride !== void 0 ? associatedObjectsColumnWidthOverride : (showCompletedDate
        ? ASSOCIATED_OBJECTS_COLUMN_WIDTH_WITH_COMPLETED_DATE
        : ASSOCIATED_OBJECTS_COLUMN_WIDTH);
    const totalWidth = COMPLETION_COLUMN_WIDTH +
        taskColumnWidth +
        (showAssignee ? ASSIGNEE_COLUMN_WIDTH : 0) +
        (showAssignedBy ? ASSIGNED_BY_COLUMN_WIDTH : 0) +
        (showAssociatedObjects ? associatedObjectsColumnWidth : 0) +
        (showDueDate ? DUE_DATE_COLUMN_WIDTH : 0) +
        (showCompletedDate ? COMPLETED_DATE_COLUMN_WIDTH : 0);
    const columns = [
        {
            key: 'completion_toggle',
            label: '',
            align: 'center',
            width: getColumnWidthPercent(COMPLETION_COLUMN_WIDTH, totalWidth),
            minWidth: COMPLETION_COLUMN_WIDTH,
            sortable: false,
        },
        {
            key: 'task',
            label: labels.task,
            width: getColumnWidthPercent(taskColumnWidth, totalWidth),
            minWidth: taskColumnWidth,
        },
    ];
    if (showAssignee) {
        columns.push({
            key: 'assignee',
            label: labels.assignee,
            width: getColumnWidthPercent(ASSIGNEE_COLUMN_WIDTH, totalWidth),
            minWidth: ASSIGNEE_COLUMN_WIDTH,
        });
    }
    if (showAssignedBy) {
        columns.push({
            key: 'assigned_by',
            label: labels.assignedBy,
            width: getColumnWidthPercent(ASSIGNED_BY_COLUMN_WIDTH, totalWidth),
            minWidth: ASSIGNED_BY_COLUMN_WIDTH,
        });
    }
    if (showAssociatedObjects) {
        columns.push({
            key: 'context',
            label: labels.associatedObjects,
            width: getColumnWidthPercent(associatedObjectsColumnWidth, totalWidth),
            minWidth: associatedObjectsColumnWidth,
        });
    }
    if (showDueDate) {
        columns.push({
            key: 'remaining',
            label: labels.due,
            align: 'center',
            width: getColumnWidthPercent(DUE_DATE_COLUMN_WIDTH, totalWidth),
            minWidth: DUE_DATE_COLUMN_WIDTH,
        });
    }
    if (showCompletedDate) {
        columns.push({
            key: 'completed',
            label: labels.completed,
            align: 'center',
            width: getColumnWidthPercent(COMPLETED_DATE_COLUMN_WIDTH, totalWidth),
            minWidth: COMPLETED_DATE_COLUMN_WIDTH,
        });
    }
    return columns;
};
const formatTaskTableToggleCount = (count) => typeof count === 'number' && count > TASK_TABLE_TOGGLE_COUNT_LIMIT
    ? `${TASK_TABLE_TOGGLE_COUNT_LIMIT}+`
    : String(count);
export const TaskTableSegmentedFilter = ({ ariaLabel, value, options, onChange, }) => (_jsx(TaskTableToggleGroup, { "aria-label": ariaLabel, role: "group", children: options.map((option) => (_jsx(TaskTableToggleButton, { type: "button", "$active": value === option.key, "aria-pressed": value === option.key, onClick: () => onChange(option.key), children: option.count === undefined
            ? option.label
            : `${option.label} (${formatTaskTableToggleCount(option.count)})` }, option.key))) }));
export const TaskTableOwnerPills = ({ names, fallbackLabel, maxVisible = MAX_VISIBLE_OWNER_PILLS, onNameClick, }) => {
    const visibleNames = names.slice(0, maxVisible);
    const hiddenNames = names.slice(maxVisible);
    const renderedNames = visibleNames.length ? visibleNames : [fallbackLabel];
    return (_jsxs(TaskTableOwnerPillsContainer, { children: [renderedNames.map((name, index) => {
                const isInteractive = visibleNames.length > 0 && !!onNameClick;
                return (_jsx(TaskTableOwnerPill, { as: isInteractive ? 'button' : 'span', type: isInteractive ? 'button' : undefined, "$interactive": isInteractive, className: isInteractive ? 'sorted-table-clickable' : undefined, title: name, onClick: isInteractive
                        ? (event) => {
                            event.stopPropagation();
                            onNameClick === null || onNameClick === void 0 ? void 0 : onNameClick(name, index);
                        }
                        : undefined, children: name }, `${name}:${index}`));
            }), hiddenNames.length ? (_jsx(Tooltip, { title: hiddenNames.length === 1 ? (_jsx(Typography, { elementTheme: "body4", children: hiddenNames[0] })) : (_jsx(TaskTableOverflowTooltipList, { children: hiddenNames.map((name, index) => (_jsx("li", { children: _jsx(Typography, { elementTheme: "body4", children: name }) }, `${name}:${index}`))) })), children: _jsxs(TaskTableOwnerPill, { className: "task-table-owner-pill-counter dashboard-context-pill-counter", title: hiddenNames.join(', '), children: ["+", hiddenNames.length] }) })) : null] }));
};
const TaskTable = (_a) => {
    var { filters, columns, minTableWidth } = _a, tableProps = __rest(_a, ["filters", "columns", "minTableWidth"]);
    const inferredMinTableWidth = columns.reduce((totalWidth, column) => { var _a; return totalWidth + ((_a = column.minWidth) !== null && _a !== void 0 ? _a : 0); }, 0);
    return (_jsxs(TaskTableContainer, { children: [filters, _jsx(SortedTable, Object.assign({}, tableProps, { columns: columns, minTableWidth: (minTableWidth !== null && minTableWidth !== void 0 ? minTableWidth : inferredMinTableWidth) || undefined, compactRows: true }))] }));
};
export default TaskTable;
